import {memo} from 'react';
import type {TFunction} from 'i18next';
import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {formatDateHM, formatFullDMY} from 'crqlar-react-common/dist/utils/date.util';
import {useTranslation} from 'react-i18next';
import {formatRecipientPreview} from '../config/recipients';
import {
    formatIdFilterNames, formatSourceList, formatStatusList, hasSourceFilter, isSourceFilterEmpty,
    readSectorFilter, readServiceFilter, readSources, readStatuses
} from '../config/reservationTriggers';
import {
    formatGuestCategoryList, readDaysAfterSent, readGuestCategoryFilter, readSurveyFilter
} from '../config/surveyTriggers';
import {formatTeamMemberFilters} from '../config/teamMembers';
import {REPORT_FORMAT_NAMES, readReportFormats, reportPeriodSummary} from '../config/surveyReport';
import {formatWaitDuration} from '../utils/waitDuration';
import IfConditionSummary from './IfConditionSummary';
import {readContinueIfGroups} from '../config/continueIf';
import NodePreview, {NodePreviewRow} from './NodePreview';

/** The `i18n` of `useTranslation`, or what a caller outside React knows of it. */
interface SummaryLanguage {
    language: string;
    resolvedLanguage?: string;
}

/** The card's rows; the go-live dialog reads triggers through them too. If / Continue if have no rows. */
export const nodeSummaryRows = (
    type: NodeType,
    config: Record<string, any> | undefined,
    t: TFunction,
    i18n: SummaryLanguage
): NodePreviewRow[] => {
    const rows: NodePreviewRow[] = [];
    const add = (text?: string | null, label?: string) => {
        if (text) rows.push({text, label});
    };

    switch (type) {
        case NodeType.FORM_SUBMITTED:
            add(config?.formTitle || t('All forms'));
            break;
        case NodeType.RESERVATION_CREATED:
        case NodeType.RESERVATION_STATUS_CHANGED: {
            const sources = readSources(config);
            if (type === NodeType.RESERVATION_STATUS_CHANGED) {
                add(formatStatusList(readStatuses(config), t), t('Status'));
            }
            if (isSourceFilterEmpty(sources)) add(t('Pick at least one source'));
            else if (hasSourceFilter(sources)) add(formatSourceList(sources!, t), t('From'));
            add(formatIdFilterNames(readSectorFilter(config), t), t('Sector'));
            add(formatIdFilterNames(readServiceFilter(config), t), t('Service'));
            if (!rows.length) add(t(type === NodeType.RESERVATION_CREATED ? 'All reservations' : 'Any status change'));
            break;
        }
        case NodeType.SURVEY_SUBMITTED:
        case NodeType.SURVEY_REMINDER_DUE: {
            add(formatIdFilterNames(readSurveyFilter(config), t) || t('Pick at least one survey'));
            if (type === NodeType.SURVEY_REMINDER_DUE) add(t('After {{days}} days', {days: readDaysAfterSent(config)}));
            const guests = readGuestCategoryFilter(config);
            if (guests.ids.length) add(formatGuestCategoryList(guests, t), t('Guests'));
            if (type === NodeType.SURVEY_SUBMITTED) add(formatIdFilterNames(readSectorFilter(config), t), t('Sector'));
            break;
        }
        case NodeType.SCHEDULED_TRIGGER: {
            const start = config?.startDate ? new Date(config.startDate) : null;
            if (!config?.scheduleType || !start || !Number.isFinite(start.getTime())) {
                add(t('No schedule configured'));
                break;
            }
            // The schedule dialog's format: dd.MM.yyyy and HH:mm.
            const time = formatDateHM(start);
            switch (config.scheduleType) {
                case 'once':
                    add(t('Once on {{date}} at {{time}}', {date: formatFullDMY(start), time}));
                    break;
                case 'daily':
                    add(t('Daily at {{time}}', {time}));
                    break;
                case 'weekly': {
                    const week = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
                    const picked = week.filter(day => config.daysOfWeek?.includes(day));
                    // None picked runs on the start date's weekday, as the schedule dialog's `startWeekday` says.
                    const days = (picked.length ? picked : [week[(start.getDay() + 6) % 7]])
                        .map(day => t(day.charAt(0) + day.slice(1).toLowerCase())).join(', ');
                    add(t('Weekly on {{days}} at {{time}}', {days, time}));
                    break;
                }
                case 'monthly':
                    add(t('Monthly on day {{day}} at {{time}}', {day: config.dayOfMonth || start.getDate(), time}));
                    break;
                default:
                    add(t('No schedule configured'));
            }
            break;
        }
        case NodeType.SEND_EMAIL:
            add(formatRecipientPreview(config, t) || t('No recipient selected'), t('To'));
            if (config?.emailMode === 'MAILEON') {
                add(config.maileonTemplateId ? String(config.maileonTemplateId) : t('No template selected'), t('Template'));
            } else add(config?.subject, t('Subject'));
            add(config?.modificationInstructions, t('AI prompt'));
            break;
        case NodeType.SEND_PUSH:
            add(formatRecipientPreview({profileId: config?.profileId}, t) || t('No recipient selected'), t('To'));
            add(config?.title || t('No notification title'), t('Title'));
            break;
        case NodeType.SURVEY_INVITE:
            add(config?.surveyName || config?.surveyId || t('No survey selected'), t('Survey'));
            break;
        case NodeType.SURVEY_REPORT: {
            add(config?.surveyName || config?.surveyId || t('No survey selected'), t('Survey'));
            const period = reportPeriodSummary(config, t);
            const files = readReportFormats(config).map(format => REPORT_FORMAT_NAMES[format]).join(', ');
            add(files ? `${period} · ${files}` : period);
            break;
        }
        case NodeType.SEND_TEAM_EMAIL:
            add(config?.attachmentSourceNodeIds?.length
                ? t('Email to each team member, with attachments')
                : t('Email to each team member'));
            add(config?.subject, t('Subject'));
            break;
        case NodeType.FULL_SYNC_AUDIENCE:
            add(t('All active audiences'));
            break;
        case NodeType.AUDIENCE:
            // A template preview names its audience before one exists, so a name alone counts; the editor
            // writes and clears `audienceId` and `audienceName` together.
            if (config?.audienceId || config?.audienceName) {
                add(config.audienceName || config.audienceId);
                if (config.marketingConsentOnly) add(t('marketing consent only'));
            } else add(t('No audience selected'));
            break;
        case NodeType.TEAM_MEMBERS:
            add(formatTeamMemberFilters(config, t, i18n.resolvedLanguage || i18n.language) || t('Nobody picked yet'));
            break;
        case NodeType.AFTER_EVERYONE:
            add(t('Waits until all runs are done, then continues once'));
            break;
        case NodeType.FINISH:
            add(t('Stops the run here'));
            break;
        case NodeType.WAIT: {
            const duration = formatWaitDuration(config, t);
            add(duration ? t('Waits {{duration}}', {duration}) : t('No duration set (waits 1 minute)'));
            break;
        }
    }

    return rows;
};

/** Card previews use stored configuration only, including the snapshots in template previews. */
const NodeSummary = ({type, config}: {type: NodeType; config?: Record<string, any>}) => {
    const {t, i18n} = useTranslation();
    if (type === NodeType.IF_CONDITION) return <IfConditionSummary config={config}/>;
    if (type === NodeType.CONTINUE_IF) return <IfConditionSummary config={{conditionGroups: readContinueIfGroups(config)}}/>;

    return <NodePreview rows={nodeSummaryRows(type, config, t, i18n)}/>;
};

export default memo(NodeSummary);
